import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { Eye, QrCode, MousePointerClick } from "lucide-react";

export default function Statistics() {
  const { id } = useParams();
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [restaurant, setRestaurant] = useState(null);

  useEffect(() => {
    api.get(`/restaurants/${id}/stats`).then((r) => setStats(r.data));
    api.get(`/restaurants/${id}`).then((r) => setRestaurant(r.data));
  }, [id]);

  if (!stats) return <DashboardLayout restaurantId={id}><div>{t("common.loading")}</div></DashboardLayout>;

  return (
    <DashboardLayout restaurantId={id}>
      <div className="space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{t("dashboard.statistics")}</div>
          <h1 className="text-3xl font-bold tracking-tight">{restaurant?.name}</h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: t("dashboard.totalViews"), value: stats.views, icon: Eye, color: "from-purple-500 to-purple-500" },
            { label: t("dashboard.totalScans"), value: stats.scans, icon: QrCode, color: "from-fuchsia-500 to-pink-500" },
            { label: "Clicks", value: stats.clicks, icon: MousePointerClick, color: "from-emerald-500 to-teal-500" },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="rounded-2xl border bg-card p-5" data-testid={`stat-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{s.label}</div>
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${s.color} grid place-items-center text-white`}>
                    <Icon className="h-4 w-4" />
                  </div>
                </div>
                <div className={`text-3xl font-bold mt-2 bg-gradient-to-br ${s.color} bg-clip-text text-transparent`}>{s.value}</div>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border bg-card p-5">
          <h3 className="font-bold mb-3">{t("stats.last7days")}</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.3} />
                <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Line type="monotone" dataKey="views" stroke="#6366F1" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-5">
          <h3 className="font-bold mb-3">{t("stats.topItems")}</h3>
          {stats.top_items.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t("stats.noData")}</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.top_items}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.3} />
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="clicks" fill="#A855F7" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
